import * as React from 'react';

import ViewStack from './components/view-stack';
import Panel, { IPanel } from './components/panel';
import Tabs from './components/tabs';

import Title, { Header } from '~/components/title';
import { ServicesContext } from '~/providers/services-context';
import { replaceOrPushHistoryState } from '~/utils/us-utils';

// Interfaces
interface IProps {
  defaultPanel: string;
  panels: Panel[];
  maxHistory: number;
  handleClose: () => void;
}

interface IState {
  currentView: IPanel;
}

// Styles
import styles from './style/myoc-account.module.css';

class MyOCAccount extends React.Component<IProps, IState> {
  static contextType = ServicesContext;

  viewStack: ViewStack;

  constructor(
  public props: IProps,
  public state: IState)
  {
    super(props, state);

    this.viewStack = new ViewStack();
  }

  // Re-intializes the view stack on ajax-page load.
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.initViewStack(nextProps);
  }

  componentDidMount() {
    this.initViewStack(this.props);
  }

  // Initializes view stack on page load.
  private initViewStack(props: IProps): void {
    const initView = props.panels.
    filter((panel) => panel.identifier === props.defaultPanel).
    shift();

    this.viewStack.pushViewToStack(initView);

    const nextCurrentView = this.viewStack.getCurrentView();
    this.setState({ currentView: nextCurrentView });
  }

  // Triggers a render of the selected panel.
  private updateCurrentView(): void {
    const nextCurrentView = this.viewStack.getCurrentView();
    this.setState({ currentView: nextCurrentView }, this.updateHistory);
  }

  private updateHistory(): void {
    const nextPath = this.state.currentView.path;
    if (!nextPath) return;

    replaceOrPushHistoryState(`myoddschecker/${nextPath}`);
  }

  // Allows panels to trigger navigation to the previous panel in the stack.
  public goBack = () => {
    this.viewStack.popCurrentView();
    this.updateCurrentView();
  };

  // Allows panels to trigger navigation to another panel.
  public goTo = (options: {
    panelIdentifier: string;
    model: unknown;
  }): void => {
    const nextView = this.props.panels.
    filter(({ identifier }) => identifier === options.panelIdentifier).
    shift();

    this.viewStack.pushViewToStack(nextView, options.model);

    // Prevent the stack from getting too large.
    if (this.viewStack.stack.length > this.props.maxHistory) {
      this.viewStack.stack.splice(0, 1);
    }

    this.updateCurrentView();
  };

  public render(): JSX.Element | null {
    const { handleClose } = this.props;
    const { currentView } = this.state;

    if (!currentView) {
      return null;
    }

    const PanelView = (() => {
      const View = currentView.component;
      if (!View) return null;

      return React.cloneElement(View, {
        goBack: this.goBack,
        goTo: this.goTo,
        model: currentView.model,
        currentView: currentView.identifier,
        handleClose
      });
    })();

    // NOTE: we need to short-circuit render here as following
    // views should not render the typical account components
    const nonStandardViews = [
    'Verification',
    'ResetPassword',
    'ExpiredLink',
    'ResendLink'];

    if (nonStandardViews.includes(currentView.identifier)) {
      return PanelView;
    }

    return (
      <div className={styles.container}>
				<Title
          title={this.context.labelActions.get('MY_ACCOUNT')}
          tag={Header.H1}
          className={styles.title}
          testId="title" />

				<div className={styles.tabs}>
					<Tabs goTo={this.goTo} currentPanel={currentView.identifier} />
				</div>
				<div className={styles.content}>{PanelView}</div>
			</div>);

  }
}

export default MyOCAccount;